import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/axios";
import { Printer } from "@/types";
import { toast } from "sonner";
import {
  Printer as PrinterIcon,
  Plus,
  RefreshCw,
  Wrench,
  CheckCircle2,
  Play,
  Pause,
  Trash2,
  Edit2,
  Sparkles,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";

const QUICK_MODELS = [
  "Bambu Lab P1S",
  "Bambu Lab A1 Mini",
  "Bambu Lab X1 Carbon",
  "Creality Ender 3 S1 Pro",
  "Creality K1 Max",
  "Prusa MK4",
  "Voron 2.4",
  "Artillery Genius",
];

export default function PrintersPage() {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Partial<Printer> | null>(null);

  useEffect(() => {
    loadPrinters();
  }, []);

  const loadPrinters = async () => {
    try {
      setIsLoading(true);
      const res = await api.get<Printer[]>("/printers");
      setPrinters(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Error al cargar las impresoras del taller");
      setPrinters([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (printerId: number, nextStatus: Printer["status"], clearJob = false) => {
    try {
      await api.patch(`/printers/${printerId}/status`, {
        status: nextStatus,
        clearCurrentJob: clearJob,
      });
      setPrinters((prev) =>
        prev.map((p) =>
          p.id === printerId
            ? {
                ...p,
                status: nextStatus,
                currentJobId: clearJob ? null : p.currentJobId,
                currentJob: clearJob ? null : p.currentJob,
              }
            : p
        )
      );
      toast.success(
        nextStatus === "maintenance"
          ? "Máquina puesta en mantenimiento"
          : nextStatus === "idle"
          ? "Impresora disponible para producir"
          : `Estado cambiado a ${nextStatus}`
      );
    } catch {
      toast.error("No se pudo actualizar el estado de la impresora");
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPrinter?.name?.trim()) {
      toast.error("El nombre de la impresora es obligatorio");
      return;
    }

    try {
      if (editingPrinter.id) {
        await api.put(`/printers/${editingPrinter.id}`, editingPrinter);
        toast.success("Impresora actualizada");
      } else {
        await api.post("/printers", {
          ...editingPrinter,
          status: editingPrinter.status || "idle",
        });
        toast.success("Nueva impresora agregada al taller");
      }
      setIsModalOpen(false);
      setEditingPrinter(null);
      loadPrinters();
    } catch {
      toast.error("Error al guardar la impresora");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("¿Seguro que deseas eliminar esta impresora?")) return;
    try {
      await api.delete(`/printers/${id}`);
      setPrinters((prev) => prev.filter((p) => p.id !== id));
      toast.success("Impresora eliminada");
    } catch {
      toast.error("Error al eliminar la impresora");
    }
  };

  const handleSeed = async () => {
    try {
      toast.loading("Inicializando máquinas del taller...");
      await api.post("/printers/seed");
      toast.dismiss();
      toast.success("Máquinas iniciales configuradas");
      loadPrinters();
    } catch {
      toast.dismiss();
      toast.error("No se pudieron cargar las impresoras iniciales");
    }
  };

  const printingCount = printers.filter((p) => p.status === "printing").length;
  const idleCount = printers.filter((p) => p.status === "idle").length;
  const maintenanceCount = printers.filter((p) => p.status === "maintenance" || p.status === "offline").length;

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Impresoras & Granja 3D</h1>
          <p className="text-muted-foreground text-sm">
            Supervisá el estado en tiempo real de cada máquina, trabajos en curso y mantenimiento.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadPrinters}
            disabled={isLoading}
            className="gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>

          {printers.length === 0 && !isLoading && (
            <Button variant="secondary" size="sm" onClick={handleSeed} className="gap-1.5">
              <Sparkles className="h-4 w-4" />
              Cargar Máquinas Taller
            </Button>
          )}

          <Button
            size="sm"
            onClick={() => {
              setEditingPrinter({
                name: "",
                model: "Bambu Lab P1S",
                status: "idle",
                notes: "",
              });
              setIsModalOpen(true);
            }}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Nueva Máquina
          </Button>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border-border/80">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Total Granja</span>
            <PrinterIcon className="h-4 w-4 text-primary" />
          </div>
          <div className="text-2xl font-bold mt-2">{printers.length}</div>
          <p className="text-[11px] text-muted-foreground mt-0.5">Máquinas registradas</p>
        </Card>

        <Card className="p-4 border-blue-500/30 bg-blue-500/5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Imprimiendo</span>
            <Play className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-2xl font-bold mt-2 text-blue-600 dark:text-blue-400">{printingCount}</div>
          <p className="text-[11px] text-muted-foreground mt-0.5">En producción activa</p>
        </Card>

        <Card className="p-4 border-emerald-500/30 bg-emerald-500/5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Disponibles</span>
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="text-2xl font-bold mt-2 text-emerald-600 dark:text-emerald-400">{idleCount}</div>
          <p className="text-[11px] text-muted-foreground mt-0.5">Listas para trabajo</p>
        </Card>

        <Card className="p-4 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Mantenimiento</span>
            <Wrench className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="text-2xl font-bold mt-2 text-amber-600 dark:text-amber-400">{maintenanceCount}</div>
          <p className="text-[11px] text-muted-foreground mt-0.5">Requieren atención</p>
        </Card>
      </div>

      {/* Grid de Impresoras */}
      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-50" />
          Cargando estado de las impresoras...
        </div>
      ) : printers.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <PrinterIcon className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <h3 className="font-semibold text-lg">No hay impresoras registradas en el taller</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Registrá tus máquinas para asignarles piezas directamente desde la cola de producción.
          </p>
          <div className="flex justify-center gap-3 mt-5">
            <Button onClick={handleSeed} variant="outline" className="gap-2">
              <Sparkles className="h-4 w-4" /> Cargar máquinas sugeridas
            </Button>
            <Button
              onClick={() => {
                setEditingPrinter({ name: "", model: "Bambu Lab P1S", status: "idle" });
                setIsModalOpen(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" /> Agregar mi primera impresora
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {printers.map((p) => {
            const isPrinting = p.status === "printing";
            const isIdle = p.status === "idle";
            const isMaint = p.status === "maintenance";

            return (
              <Card
                key={p.id}
                className={`transition-all duration-200 border overflow-hidden flex flex-col justify-between ${
                  isPrinting
                    ? "border-blue-500/50 shadow-sm shadow-blue-500/10 bg-gradient-to-b from-blue-500/5 to-card"
                    : isMaint
                    ? "border-amber-500/40 bg-gradient-to-b from-amber-500/5 to-card"
                    : "border-border/80 bg-card hover:border-primary/40"
                }`}
              >
                <div>
                  {/* Card Header */}
                  <div className="p-5 pb-3 flex justify-between items-start gap-2 border-b border-border/50">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <PrinterIcon className="h-4 w-4 text-primary shrink-0" />
                        <h3 className="font-bold text-base leading-snug">{p.name}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">
                        {p.model || "Impresora 3D"}
                      </p>
                    </div>

                    <Badge
                      variant="outline"
                      className={`text-xs capitalize font-medium flex items-center gap-1.5 px-2.5 py-0.5 ${
                        isPrinting
                          ? "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30 animate-pulse"
                          : isIdle
                          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                          : isMaint
                          ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          isPrinting
                            ? "bg-blue-500"
                            : isIdle
                            ? "bg-emerald-500"
                            : isMaint
                            ? "bg-amber-500"
                            : "bg-muted-foreground"
                        }`}
                      />
                      {isPrinting
                        ? "Imprimiendo"
                        : isIdle
                        ? "Libre"
                        : isMaint
                        ? "Mantenimiento"
                        : "Apagada"}
                    </Badge>
                  </div>

                  {/* Card Body: Current Job or Status details */}
                  <div className="p-5 space-y-4">
                    {isPrinting && p.currentJob ? (
                      <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 space-y-2">
                        <div className="flex justify-between items-start">
                          <span className="text-[10px] uppercase tracking-wider font-bold text-blue-700 dark:text-blue-300">
                            Trabajo en Máquina
                          </span>
                          <span className="text-[10px] font-mono text-muted-foreground">
                            Job #{p.currentJob.id}
                          </span>
                        </div>
                        <p className="font-semibold text-sm line-clamp-1">
                          {p.currentJob.fileName || "Pieza en proceso"}
                        </p>
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          {p.currentJob.material && (
                            <span className="bg-background/80 px-2 py-0.5 rounded border border-border/60">
                              {p.currentJob.material}
                            </span>
                          )}
                          {p.currentJob.color && (
                            <span className="bg-background/80 px-2 py-0.5 rounded border border-border/60">
                              Color: {p.currentJob.color}
                            </span>
                          )}
                          {p.currentJob.gramsEstimated && (
                            <span className="bg-background/80 px-2 py-0.5 rounded border border-border/60">
                              ~{p.currentJob.gramsEstimated}g
                            </span>
                          )}
                        </div>
                      </div>
                    ) : isPrinting && !p.currentJob ? (
                      <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/20 text-xs text-muted-foreground">
                        Imprimiendo trabajo manual o externo.
                      </div>
                    ) : isIdle ? (
                      <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-xs text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                        <span>Cama limpia y calibrada. Lista para nuevo lote.</span>
                      </div>
                    ) : (
                      <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                        <span>En pausa o revisión técnica. No asignar trabajos.</span>
                      </div>
                    )}

                    {p.notes && (
                      <div className="text-xs text-muted-foreground bg-muted/30 p-2.5 rounded-lg">
                        <span className="font-medium text-foreground">Notas: </span>
                        {p.notes}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer: Quick Actions */}
                <div className="p-4 pt-0 border-t border-border/40 mt-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {isIdle && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-500/10 h-8 px-2"
                        onClick={() => handleStatusChange(p.id, "maintenance")}
                        title="Marcar para mantenimiento"
                      >
                        <Wrench className="h-3.5 w-3.5 mr-1" />
                        Mantenimiento
                      </Button>
                    )}

                    {isMaint && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10 h-8 px-2"
                        onClick={() => handleStatusChange(p.id, "idle")}
                        title="Liberar máquina"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                        Poner Libre
                      </Button>
                    )}

                    {isPrinting && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-muted-foreground hover:bg-muted h-8 px-2"
                        onClick={() => handleStatusChange(p.id, "idle", true)}
                        title="Liberar máquina manualmente"
                      >
                        <Pause className="h-3.5 w-3.5 mr-1" />
                        Liberar
                      </Button>
                    )}

                    <Button asChild size="sm" variant="ghost" className="h-8 px-2 text-xs">
                      <Link to="/workshop">
                        Taller <ArrowRight className="h-3 w-3 ml-1 opacity-70" />
                      </Link>
                    </Button>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setEditingPrinter(p);
                        setIsModalOpen(true);
                      }}
                      title="Editar impresora"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(p.id)}
                      title="Eliminar impresora"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Crear / Editar Impresora */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPrinter?.id ? "Editar Impresora" : "Nueva Máquina 3D"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nombre / Identificador en Taller *</Label>
              <Input
                required
                placeholder="Ej. Bambu Lab P1S #1"
                value={editingPrinter?.name || ""}
                onChange={(e) =>
                  setEditingPrinter((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label>Modelo de Impresora</Label>
              <Input
                placeholder="Ej. Bambu Lab P1S, Ender 3, etc."
                value={editingPrinter?.model || ""}
                onChange={(e) =>
                  setEditingPrinter((prev) => ({ ...prev, model: e.target.value }))
                }
              />
              <div className="flex flex-wrap gap-1.5 pt-1.5">
                {QUICK_MODELS.map((model) => (
                  <button
                    key={model}
                    type="button"
                    onClick={() =>
                      setEditingPrinter((prev) => ({ ...prev, model }))
                    }
                    className="text-[11px] bg-muted/60 hover:bg-muted border border-border/60 px-2 py-0.5 rounded-md transition-colors"
                  >
                    {model}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Estado Inicial</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={editingPrinter?.status || "idle"}
                onChange={(e) =>
                  setEditingPrinter((prev) => ({
                    ...prev,
                    status: e.target.value as Printer["status"],
                  }))
                }
              >
                <option value="idle">Libre / Disponible</option>
                <option value="printing">Imprimiendo</option>
                <option value="maintenance">Mantenimiento</option>
                <option value="offline">Apagada</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label>Notas / Equipamiento</Label>
              <Input
                placeholder="Ej. Boquilla 0.4mm endurecida, Cama PEI dorada..."
                value={editingPrinter?.notes || ""}
                onChange={(e) =>
                  setEditingPrinter((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>

            <DialogFooter className="pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingPrinter(null);
                }}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingPrinter?.id ? "Guardar Cambios" : "Crear Impresora"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
